// VideoCard.js
// -----------------------------------------------------------------------------
// Presentational sermon/video card for the NHV site. Renders a YouTube
// thumbnail, title, and optional published date, linking out to the video.
//
// Data contract (kept identical to the previous implementation so the page does
// not need a new shape): the `info` prop is the array of childNodes of a single
// YouTube Atom-feed <entry> element. Historically the page read:
//   - info[3].textContent -> yt:videoId
//   - info[7].textContent -> title
// This component preserves those exact reads but resolves fields defensively by
// tag name first (falling back to the legacy positional index), so it stays
// correct even if the feed's whitespace/text nodes shift. It also surfaces the
// <published> date when present.
//
// Props:
//   - info  : array  (required)  childNodes of a feed <entry> element
//   - sx    : object (default {}) extra Joy sx overrides
// -----------------------------------------------------------------------------

import * as React from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import AspectRatio from '@mui/joy/AspectRatio';
import Typography from '@mui/joy/Typography';
import Box from '@mui/joy/Box';

// Legacy positional indices used by the original component. Used only as a
// fallback when a node cannot be located by tag name.
const LEGACY_INDEX = {
  videoId: 3,
  title: 7,
  published: 13,
};

// Zero-trust: pull a single field's text from the entry childNodes. Tries the
// real tag name first, then the legacy positional index, then returns ''.
const getEntryField = (nodes, tagName, legacyIndex) => {
  if (!nodes) {
    return '';
  }

  // Tag-name lookup (robust to whitespace/text-node shifts in the feed).
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    const name =
      node && (node.nodeName || node.tagName || node.localName);
    if (typeof name === 'string' && name.toLowerCase() === tagName.toLowerCase()) {
      const text = node.textContent;
      if (typeof text === 'string' && text.trim().length > 0) {
        return text.trim();
      }
    }
  }

  // Legacy positional fallback (preserves original behavior exactly).
  const legacyNode =
    typeof legacyIndex === 'number' ? nodes[legacyIndex] : undefined;
  const legacyText = legacyNode && legacyNode.textContent;
  return typeof legacyText === 'string' ? legacyText.trim() : '';
};

// Zero-trust: only allow a plausible YouTube id (alphanumeric, dash, underscore)
// into URLs so we never interpolate arbitrary untrusted strings into a link/src.
const sanitizeVideoId = (rawId) => {
  if (typeof rawId !== 'string') {
    return '';
  }
  return /^[A-Za-z0-9_-]{6,20}$/.test(rawId) ? rawId : '';
};

// Format an ISO date string into a friendly label; degrade to '' on failure.
const formatPublished = (rawDate) => {
  if (typeof rawDate !== 'string' || rawDate.length === 0) {
    return '';
  }
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const VideoCard = ({ info, sx = {} }) => {
  // Validate at the boundary: degrade to a disabled placeholder if the entry
  // is missing or yields no usable video id.
  const videoId = sanitizeVideoId(
    getEntryField(info, 'yt:videoId', LEGACY_INDEX.videoId),
  );
  const rawTitle = getEntryField(info, 'title', LEGACY_INDEX.title);
  const title = rawTitle.length > 0 ? rawTitle : 'Untitled sermon';
  const publishedLabel = formatPublished(
    getEntryField(info, 'published', LEGACY_INDEX.published),
  );

  const watchUrl = videoId
    ? `https://www.youtube.com/watch?v=${videoId}`
    : null;
  const thumbnailUrl = videoId
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    : null;

  const interactive = Boolean(watchUrl);

  // Local handler keeps the outbound nav at the narrowest scope. Opens the
  // sanitized URL in a new tab with no opener reference.
  const handleOpen = () => {
    if (watchUrl) {
      window.open(watchUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Card
      variant="outlined"
      onClick={interactive ? handleOpen : undefined}
      role={interactive ? 'link' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleOpen();
              }
            }
          : undefined
      }
      sx={{
        width: '100%',
        p: 0,
        overflow: 'hidden',
        borderRadius: 'lg',
        boxShadow: 'sm',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        cursor: interactive ? 'pointer' : 'default',
        opacity: interactive ? 1 : 0.6,
        '&:hover': interactive
          ? {
              transform: 'translateY(-4px)',
              boxShadow: 'lg',
              borderColor: 'primary.500',
            }
          : undefined,
        '&:focus-visible': {
          outline: '3px solid',
          outlineColor: 'primary.400',
          outlineOffset: '2px',
        },
        ...sx,
      }}
    >
      <CardOverflow>
        <AspectRatio ratio="16/9" sx={{ borderRadius: 0 }}>
          {thumbnailUrl ? (
            <img src={thumbnailUrl} loading="lazy" alt={`Thumbnail for ${title}`} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.level2',
                color: 'text.tertiary',
              }}
            >
              <Typography level="body-sm">Video unavailable</Typography>
            </Box>
          )}
        </AspectRatio>
      </CardOverflow>

      <CardContent sx={{ p: 2, gap: 0.5 }}>
        <Typography
          level="title-md"
          sx={{
            color: 'text.primary',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {title}
        </Typography>

        {publishedLabel ? (
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            {publishedLabel}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
};

VideoCard.propTypes = {
  // The childNodes array of a YouTube Atom-feed <entry>. Array-like; validated
  // defensively at runtime, so we accept array | object (NodeList-like).
  info: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  sx: PropTypes.object,
};

export default VideoCard;
